import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';

import { getValidAccessToken } from '@/auth/refreshCoordinator';
import { getAccessToken } from '@/auth/tokenStore';
import { env } from '@/config/env';
import { ACCOUNT_ENDPOINTS, AUTH_ENDPOINTS } from '@/constants/api';

declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    _retry?: boolean;
  }
}

const PUBLIC_AUTH_PATHS: string[] = [
  AUTH_ENDPOINTS.register,
  AUTH_ENDPOINTS.verifyEmail,
  AUTH_ENDPOINTS.login,
  AUTH_ENDPOINTS.google,
  AUTH_ENDPOINTS.linkedin,
  AUTH_ENDPOINTS.refresh,
  ACCOUNT_ENDPOINTS.forgotPassword,
  ACCOUNT_ENDPOINTS.resetPassword,
];

function isPublicAuthPath(url: string | undefined): boolean {
  if (!url) return false;
  return PUBLIC_AUTH_PATHS.some((path) => url.startsWith(path));
}

/**
 * The single REST client for the sanctioned REST surfaces — auth, account self-service, file
 * uploads (ADR-0002). `withCredentials: true` sends/receives the backend's `HttpOnly` refresh
 * cookie (ADR-0006) on the `/auth/*` calls that rotate it. `X-Client-Type: web` tells the backend
 * to use cookie transport for the refresh token (mobile omits it and keeps the JSON body transport).
 */
export const httpClient = axios.create({
  baseURL: env.gatewayUrl,
  withCredentials: true,
  timeout: 15_000,
  headers: { 'X-Client-Type': 'web' },
});

httpClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();
  if (token && !isPublicAuthPath(config.url)) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

httpClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isPublicAuthPath(originalRequest.url) &&
      originalRequest.url !== AUTH_ENDPOINTS.logout
    ) {
      originalRequest._retry = true;
      const refreshedToken = await getValidAccessToken({ forceRefresh: true });
      if (refreshedToken) {
        originalRequest.headers.set('Authorization', `Bearer ${refreshedToken}`);
        return httpClient(originalRequest);
      }
    }

    return Promise.reject(error);
  },
);
